import { Injectable } from '@angular/core';

import { ArcUserConfig, SportsUserConfig } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { IProfileConfiguration } from '@frontend/sports/types/components/a-r-c';
import {
    UserAutologout24HoursEvent,
    UserAutologoutEvent,
    UserEvent,
    UserLoginEvent,
    UserLogoutEvent,
    UserPreHooksLoginEvent,
    UserSessionExpiredEvent,
    UserService as VanillaUserService,
} from '@frontend/vanilla/core';
import { Observable, filter } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private readonly accountCategoryKey = 'http://api.bwin.com/v3/user/accountcategoryId';
    private readonly pgAccountIdKey = 'http://api.bwin.com/v3/user/pg/nameidentifier';

    get isQuickDepositEnabled(): boolean {
        return this.sportsUser.isQuickDepositEnabled;
    }

    get columnSize(): number {
        return this.sportsUser.columnSize ?? 0;
    }

    get jurisdiction(): string {
        return this.sportsUser.jurisdiction;
    }

    get clientIP(): string {
        return this.sportsUser.clientIP;
    }

    get isEarlyPayoutAllowed(): boolean {
        return this.sportsUser.isEarlyPayoutAllowed;
    }

    get events(): Observable<UserEvent> {
        return this.vUser.events;
    }

    get isAuthenticated(): boolean {
        return this.vUser.isAuthenticated;
    }

    get currency(): string {
        return this.vUser.currency;
    }

    get ssoToken(): string | null {
        return this.vUser.ssoToken;
    }

    get geoCountry(): string {
        return this.vUser.geoCountry || '';
    }

    get country(): string {
        return this.vUser.country || '';
    }

    get username(): string | null {
        return this.vUser.username;
    }

    get workflowType(): number {
        return this.vUser.workflowType;
    }

    get id(): string | null {
        return this.vUser.id;
    }

    get accountId(): string | null {
        return this.vUser.accountId;
    }

    get displayName(): string {
        return this.vUser.displayName;
    }

    get isEligibleForNewCustomerOffer(): boolean {
        return this.sportsUser.isEligibleForNewCustomerOffer;
    }

    get arcPlayBreak(): boolean {
        return this.arcUser.arcPlayBreak;
    }

    get arcProfileDetails(): Readonly<IProfileConfiguration> {
        return this.arcUser.arcProfileDetails;
    }

    get associatedAccount(): string | undefined {
        return this.sportsUser.associatedAccount;
    }

    constructor(
        private vUser: VanillaUserService,
        private sportsUser: SportsUserConfig,
        private arcUser: ArcUserConfig,
    ) {}

    get isAnonymous(): boolean {
        const accountType = this.vUser.claims.get('accbusinessphase');

        return !accountType || accountType === 'anonymous';
    }

    readonly onUserLogin$ = this.vUser.events.pipe(filter((e) => e instanceof UserPreHooksLoginEvent));
    readonly onUserLogInWithSettings$ = this.vUser.events.pipe(filter((e) => e instanceof UserLoginEvent));
    readonly onUserLogout$ = this.vUser.events.pipe(filter((e) => e instanceof UserLogoutEvent));
    readonly onAuthenticationChange$ = this.vUser.events.pipe(
        filter(
            (e) =>
                e instanceof UserLoginEvent ||
                e instanceof UserLogoutEvent ||
                e instanceof UserAutologoutEvent ||
                e instanceof UserSessionExpiredEvent ||
                e instanceof UserAutologout24HoursEvent,
        ),
    );

    accountCategoryId(): Nullable<string> {
        return this.vUser.claims.get(this.accountCategoryKey);
    }

    getStateOrProvince(): Nullable<string> {
        return this.vUser.claims.get('stateorprovince');
    }

    getIsStakeLimitSet(): boolean {
        return this.vUser.claims.get('isSportsLimitSet')?.toLowerCase() === 'true';
    }

    getUserMobileNumber(): Nullable<string> {
        return this.vUser.claims.get('mobilephone');
    }

    getPGAccountId(): Nullable<string> {
        return this.vUser.claims.get(this.pgAccountIdKey);
    }
}
